import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import BrandAdLoader from "web/react/components/brand-ad-loader/brand-ad-loader";
import { CuratedAds } from "web/react/components/curated-ads/curated-ads";
import FeedCountAndSort from "web/react/components/feed-count-and-sort/feed-count-and-sort";
import { FeedGrid } from "web/react/components/feed-grid/feed-grid";
import { FeedHeader } from "web/react/components/feed-header/feed-header";
import { FilterContainerEmo } from "web/react/components/filters-emo/filter-container-emo";
import { MembershipPromoOverlay } from "web/react/components/membership-promo-overlay";
import { MoreLikeThisRail } from "web/react/components/more-like-this-rail/more-like-this-rail";
import { ProductOverlay } from "web/react/components/product-overlay";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedRail } from "web/react/components/related-rail/related-rail";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { SaveSearchButton } from "web/react/components/save-search-button/save-search-button";
import { View } from "web/react/emo/view";
import { setFeedBreadcrumbs } from "web/redux/ducks/feed-breadcrumbs";
import store, { ReduxStoreState } from "web/redux/store";
import environment from "web/script/modules/environment";
import { languageIsEnglish } from "web/script/modules/language";
import userProfiler from "web/script/modules/userprofiler";
import { getAdDisplayPreferences } from "web/script/utils/ads";
import {
    AdsenseForShoppingSerializer,
    CuratedAdsSerializer,
    FeedHeaderSerializer,
    FeedNavigationSerializer,
    ProductFeedSerializer,
    RecentlyViewedSerializer,
    RelatedLinksSerializer,
    RelatedRailSerializer,
} from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";
import * as styles from "./feed.css";
import { FeedPage, getFeedSubTitle, getFeedTitle } from "./utils";

interface FeedProps {
    header: FeedHeaderSerializer;
    universalFilters: UniversalFiltersData;
    productFeed: ProductFeedSerializer;
    adsenseForShopping: AdsenseForShoppingSerializer;
    feedNavigation: FeedNavigationSerializer;
    relatedSearchesRail: RelatedLinksSerializer;
    relatedDesignersRail: RelatedRailSerializer;
    relatedRetailersRail: RelatedRailSerializer;
    relatedStoreBrandsRail: RelatedRailSerializer;
    recentlyViewedRail: RecentlyViewedSerializer;
    curatedAds: CuratedAdsSerializer;
}

export function Feed({
    header,
    universalFilters,
    productFeed,
    adsenseForShopping,
    curatedAds,
    feedNavigation,
    relatedSearchesRail,
    relatedDesignersRail,
    relatedRetailersRail,
    relatedStoreBrandsRail,
    recentlyViewedRail,
}: FeedProps): React.ReactElement {
    const overlayRef = useRef(null);

    const feedPageType = environment.get("feedType") as FeedPage;
    const feedType = productFeed.type || "";
    const { isLoggedIn } = userProfiler;
    const blurFeed = feedPageType === FeedPage.WISH_LIST && !isLoggedIn();

    useEffect(() => {
        store.dispatch(setFeedBreadcrumbs(feedNavigation?.shop_breadcrumbs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feedCountState = useSelector((state: ReduxStoreState) => state.feedCount);
    const productCount = feedCountState.product_count ?? productFeed.feed_count?.product_count ?? 0;
    const retailerCount =
        feedCountState.retailer_count ?? productFeed.feed_count?.retailer_count ?? 0;

    const feedHeaderState = useSelector((state: ReduxStoreState) => state.feedHeader);
    const feedHeader = feedHeaderState.gender ? feedHeaderState : header;

    const moreLikeThisState = useSelector((state: ReduxStoreState) => state.feedMoreLikeThis);
    const moreLikeThisProducts =
        moreLikeThisState.feed_cards.length > 0
            ? moreLikeThisState.feed_cards
            : productFeed.more_like_this_rail?.feed_cards;

    const mainTitle = getFeedTitle(feedPageType, feedHeader, universalFilters);
    const subtitle = getFeedSubTitle(feedPageType);
    const actions =
        (feedPageType === FeedPage.SEARCH && languageIsEnglish() && (
            <SaveSearchButton
                term={feedHeader.title ?? ""}
                gender={feedHeader.gender}
                next={feedHeader.search_next ?? ""}
                variant="emo"
            />
        )) ||
        undefined;

    const { showCuratedAds, showAdsense } = getAdDisplayPreferences(
        curatedAds,
        null,
        adsenseForShopping
    );

    const shortDescription: string | null =
        feedPageType !== FeedPage.RELATED_PRODUCTS ? feedHeader.short_description : null;
    const description: string | null =
        feedPageType !== FeedPage.RELATED_PRODUCTS ? feedHeader.description : null;

    const redesignOverlay = environment.getFeature("cw_pdp_redesign_overlay");

    return (
        <>
            {redesignOverlay && (
                <div ref={overlayRef}>
                    <ProductOverlay parentRef={overlayRef} isOnFeed />
                </div>
            )}
            {/* Note: We're still using the `header` from the server and not redux */}
            {header?.brand_ad && (
                <div
                    className={clsx({
                        [styles.brandAd["base"]]: !header.brand_ad?.show_redesign,
                        [styles.brandAd["redesign"]]: header.brand_ad?.show_redesign,
                    })}
                >
                    <div className={styles.feedGrid}>
                        <BrandAdLoader {...header?.brand_ad} />
                    </div>
                </div>
            )}
            <div className={styles.feed}>
                <div className={clsx(styles.feedGrid, { [styles.blurFeed]: blurFeed })}>
                    <FeedHeader
                        title={mainTitle}
                        subtitle={subtitle}
                        profile={feedHeader}
                        actions={actions}
                        shortDescription={shortDescription}
                        description={description}
                        type={feedPageType}
                        productCount={productCount}
                        retailerCount={retailerCount}
                    />
                    <FilterContainerEmo
                        filters={universalFilters}
                        productCount={productCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                    />
                    <FeedCountAndSort
                        productCount={productCount}
                        retailerCount={retailerCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                    />
                </div>
                {blurFeed && (
                    <View
                        width="full"
                        height="full"
                        marginBottom="xxxl"
                        className={styles.feedGrid}
                    >
                        <MembershipPromoOverlay />
                    </View>
                )}

                <div className={clsx(styles.feedGrid, { hidden: blurFeed })}>
                    <FeedGrid
                        data={productFeed}
                        description={description}
                        feedType={feedPageType}
                        inFeedBrandAd={productFeed?.in_feed_brand_ad}
                    />

                    <div className={styles.feedExtraContent}>
                        {showCuratedAds && (
                            <CuratedAds
                                {...(curatedAds as CuratedAdsSerializer)}
                                feedType={feedType}
                            />
                        )}
                        {showAdsense && (
                            <AdsenseShopping
                                {...(adsenseForShopping as AdsenseForShoppingSerializer)}
                            />
                        )}
                        {moreLikeThisProducts && moreLikeThisProducts.length > 0 && (
                            <MoreLikeThisRail products={moreLikeThisProducts} feedType={feedType} />
                        )}
                        {recentlyViewedRail && (
                            <RecentlyViewed
                                feedType={feedType}
                                title={recentlyViewedRail.title}
                                current_product_id={recentlyViewedRail.current_product_id}
                                in_overlay={recentlyViewedRail.in_overlay}
                                products={recentlyViewedRail.products}
                                is_loading={recentlyViewedRail.is_loading}
                            />
                        )}
                        {relatedSearchesRail?.links.length > 0 && (
                            <RelatedSearchesRail
                                relatedSearches={relatedSearchesRail.links}
                                title={relatedSearchesRail.title}
                            />
                        )}
                        {relatedDesignersRail?.candidates && (
                            <RelatedRail
                                title={relatedDesignersRail.title}
                                candidates={relatedDesignersRail.candidates}
                                analyticsLabel={"designers"}
                            />
                        )}
                        {relatedStoreBrandsRail?.candidates && (
                            <RelatedRail
                                title={relatedStoreBrandsRail.title}
                                candidates={relatedStoreBrandsRail.candidates}
                                analyticsLabel={"designers"}
                            />
                        )}
                        {relatedRetailersRail?.candidates && (
                            <RelatedRail
                                title={relatedRetailersRail.title}
                                candidates={relatedRetailersRail.candidates}
                                analyticsLabel={"retailers"}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
